import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://allchinabuyspreadsheet.fit';

export default function GuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <article>
          {/* Hero */}
          <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100">
            <div className="px-4 md:px-6 max-w-4xl mx-auto text-center">
              <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line text-[10px]"></i>
                <span className="text-foreground-700 font-medium">AllChinaBuy Spreadsheet Guide</span>
              </nav>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                The Ultimate AllChinaBuy Spreadsheet Guide for Smart Buyers in 2026
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed mb-6">
                A comprehensive walkthrough of how to use the AllChinaBuy spreadsheet to find verified sellers, quality-checked products, and the best deals across 14 categories — from shoes and hoodies to accessories and perfumes.
              </p>
              <div className="flex items-center justify-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  Updated June 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-book-open-line"></i>
                  12 min read
                </span>
              </div>
              <div className="mt-8">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Open AllChinaBuy Sheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </section>

          {/* Featured Image */}
          <section className="py-8 md:py-12 bg-background-100">
            <div className="px-4 md:px-6 max-w-4xl mx-auto">
              <div className="rounded-2xl overflow-hidden h-64 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Detailed%20flatlay%20of%20various%20fashion%20products%20including%20sneakers%20hoodies%20jackets%20and%20accessories%20arranged%20on%20cream%20background%20with%20notebook%20and%20checklist%2C%20warm%20natural%20lighting%2C%20minimalist%20lifestyle%20aesthetic%2C%20editorial%20product%20photography%20with%20rich%20textures%20and%20soft%20shadows%2C%20organized%20composition%20showing%20variety%20of%20items&width=1600&height=900&seq=guide-hero-2026&orientation=landscape"
                  alt="AllChinaBuy Spreadsheet shopping guide with products"
                  className="w-full h-full object-cover object-top"
                  loading="eager"
                />
              </div>
            </div>
          </section>

          {/* Article Content */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="px-4 md:px-6 max-w-4xl mx-auto">
              <div>

                {/* Section 1 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    What Exactly Is the AllChinaBuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    The <strong>AllChinaBuy spreadsheet</strong> is a meticulously curated database that brings together trusted product links, verified seller information, and organized shopping categories into one accessible document. Think of it as your personal shopping assistant — instead of scrolling through endless listings and wondering which sellers are reliable, the spreadsheet does the heavy lifting for you.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Built and maintained by a community of experienced buyers, the <strong>best AllChinaBuy spreadsheet</strong> covers 14 distinct product categories, each populated with hand-picked items that have been vetted for quality, pricing accuracy, and seller reliability. Whether you are hunting for the latest sneaker releases, premium hoodies, stylish jackets, or niche accessories, the spreadsheet organizes everything so you can find what you need in seconds rather than hours.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    The <strong>AllChinaBuy spreadsheet 2026</strong> edition reflects the most current data available, with daily updates ensuring that product links remain active, seller statuses stay accurate, and new trending items are added as they emerge. This commitment to freshness is what sets a quality <strong>AllChinaBuy spreadsheet guide</strong> apart from static, outdated lists that lose their value within weeks.
                  </p>
                </div>

                {/* Section 2 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    Why a Spreadsheet Beats Searching On Your Own
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    If you have ever spent an afternoon digging through product pages, comparing prices across different sellers, and trying to figure out which listings are legitimate, you know how exhausting independent searching can be. The <strong>AllChinaBuy spreadsheet</strong> eliminates that friction entirely.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    With the spreadsheet, every product is already categorized, priced, and linked to a verified seller. You do not need to cross-reference multiple tabs, worry about outdated links, or second-guess whether a seller is trustworthy. The community review system built into the <strong>AllChinaBuy spreadsheet links</strong> ecosystem means that sellers earn their place on the list through consistent positive feedback — not through advertising or self-promotion.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    For buyers who value their time, the decision is clear. The spreadsheet condenses what would normally be 3-4 hours of research into a 10-minute browsing session. Multiply that by the number of times you shop in a year, and the time savings alone make the <strong>AllChinaBuy spreadsheet</strong> an invaluable resource.
                  </p>
                </div>

                {/* Section 3 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    The 14 Categories Inside the AllChinaBuy Spreadsheet
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    One of the standout features of the spreadsheet is its organizational structure. Rather than dumping hundreds of products into a single overwhelming list, the <strong>AllChinaBuy spreadsheet</strong> divides everything into 14 clean categories:
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    {[
                      { name: 'Shoes', desc: 'Sneakers, casual shoes, and athletic footwear from verified sellers with size guides and QC photos.' },
                      { name: 'Hoodies / Sweaters', desc: 'Premium hoodies, crewnecks, and sweaters in various styles and colorways.' },
                      { name: 'T-Shirts', desc: 'Graphic tees, basics, and designer-inspired t-shirts at competitive prices.' },
                      { name: 'Jackets', desc: 'Bombers, denim jackets, puffers, and seasonal outerwear for every budget.' },
                      { name: 'Pants / Shorts', desc: 'Jeans, cargo pants, joggers, and shorts from trusted clothing sellers.' },
                      { name: 'Headwear', desc: 'Caps, beanies, bucket hats, and snapbacks to complete any outfit.' },
                      { name: 'Sets', desc: 'Matching tracksuits, co-ord sets, and bundled clothing collections.' },
                      { name: 'Underwear / Underpants', desc: 'Quality basics and branded undergarments at affordable price points.' },
                      { name: 'Jersey', desc: 'Sports jerseys, retro kits, and fan merchandise from reliable sources.' },
                      { name: 'Accessories', desc: 'Watches, sunglasses, jewelry, phone cases, and lifestyle accessories.' },
                      { name: 'Bags', desc: 'Backpacks, crossbody bags, totes, and travel bags in various designs.' },
                      { name: 'Belts', desc: 'Leather belts, designer-inspired buckles, and casual belt options.' },
                      { name: 'Perfumes', desc: 'Fragrances, colognes, and perfume alternatives with seller ratings.' },
                      { name: 'Other Stuff', desc: 'Unique finds, seasonal items, and miscellaneous products worth checking out.' },
                    ].map((cat) => (
                      <div key={cat.name} className="bg-background-100 rounded-xl p-4 border border-background-200/40">
                        <h4 className="text-sm font-semibold text-foreground-900 mb-1">{cat.name}</h4>
                        <p className="text-xs text-foreground-600 leading-relaxed">{cat.desc}</p>
                      </div>
                    ))}
                  </div>

                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    Each category in the <strong>AllChinaBuy spreadsheet</strong> is independently maintained, meaning that if a particular seller stops being reliable in the Shoes category, only that section is affected — the rest of the spreadsheet remains trustworthy. This modular approach to quality control is one reason experienced buyers return to the spreadsheet again and again.
                  </p>
                </div>

                {/* Section 4 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    How to Verify AllChinaBuy Spreadsheet Sellers
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Seller verification is the backbone of any trustworthy <strong>AllChinaBuy spreadsheet</strong>. Before a seller is added to the list, they typically go through a multi-step vetting process that includes community feedback analysis, order history review, and product quality checks.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    When browsing <strong>AllChinaBuy spreadsheet sellers</strong>, pay attention to these key signals: the number of completed orders associated with the seller, the average customer rating, the consistency of product quality across different batches, and the responsiveness of the seller to buyer inquiries. Sellers who score well across all four criteria are marked as verified and highlighted prominently in the spreadsheet.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    It is also worth noting that the <strong>AllChinaBuy spreadsheet</strong> community actively flags sellers who show signs of declining quality or suspicious behavior. This dynamic feedback loop means the spreadsheet is constantly self-correcting — sellers who fail to maintain standards are removed, while new high-quality sellers are added as they earn community trust. This is a living document, not a static list, and that makes all the difference for buyers seeking <strong>AllChinaBuy spreadsheet safe</strong> shopping experiences.
                  </p>
                </div>

                {/* Section 5 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    Quality Control: The AllChinaBuy Spreadsheet QC Process
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Quality control is a cornerstone of the <strong>AllChinaBuy spreadsheet QC</strong> framework. Every product listed in the spreadsheet is evaluated against a standardized set of criteria before being recommended to buyers. This includes accuracy of product photos versus the actual item received, build quality and material consistency, packaging and shipping condition upon arrival, and adherence to advertised specifications.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    The QC data within the spreadsheet allows buyers to see at a glance which products have passed rigorous quality checks and which ones have received mixed feedback. Products that consistently receive positive QC ratings are given priority placement within their respective categories, making it easy to identify the safest bets when you are ready to make a purchase.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    For buyers who are new to using a spreadsheet for shopping, the QC ratings serve as an invaluable shortcut. Rather than developing your own quality assessment skills over dozens of purchases, you can lean on the collective experience of the <strong>AllChinaBuy spreadsheet</strong> community — hundreds of buyers who have already done the testing and shared their findings.
                  </p>
                </div>

                {/* Section 6 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    Shipping Tips for AllChinaBuy Spreadsheet Users
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    International shipping can be one of the most confusing aspects of buying products through the <strong>AllChinaBuy spreadsheet</strong>, but the spreadsheet itself includes shipping information that simplifies the process significantly. Each seller listing typically includes estimated delivery times, available shipping methods, and any region-specific restrictions.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Pro tip: when ordering from multiple sellers listed in the <strong>AllChinaBuy spreadsheet</strong>, consider consolidating your shipments when possible. Some sellers offer combined shipping discounts for multi-item orders, and using a consolidation service can reduce overall shipping costs by 20-40% compared to shipping each item individually.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    The spreadsheet also includes notes on customs handling for different destination countries, helping you anticipate any additional fees or documentation requirements before you place an order. This level of detail is what transforms the <strong>AllChinaBuy spreadsheet</strong> from a simple product list into a complete shopping companion.
                  </p>
                </div>

                {/* Section 7 */}
                <div className="mb-12">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    Staying Safe: AllChinaBuy Spreadsheet Safety Practices
                  </h2>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    Safety is a top concern for anyone using the <strong>AllChinaBuy spreadsheet</strong>, and the platform has built-in safeguards that help protect buyers. The most important rule is simple: always transact through the official platform rather than sending direct payments to sellers. This ensures that buyer protection policies apply to your purchase.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                    The spreadsheet marks sellers with strong track records using a verification badge system. Sellers without verification badges should be approached with extra caution — read their reviews carefully, check how long they have been active, and consider starting with a smaller test order before committing to a larger purchase. Following these <strong>AllChinaBuy spreadsheet safe</strong> buying practices significantly reduces the risk of encountering problems.
                  </p>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    Additionally, the spreadsheet community maintains an active discussion thread where buyers share their recent experiences — both positive and negative. Checking this thread before making a purchase from a seller you have not used before is a quick and effective way to gather real-time safety intelligence from fellow buyers.
                  </p>
                </div>

                {/* Section 8 - Final CTA */}
                <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/60 text-center">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                    Ready to Start Using the AllChinaBuy Spreadsheet?
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6 max-w-lg mx-auto">
                    The best AllChinaBuy spreadsheet is just a click away. Start exploring verified products, trusted sellers, and organized categories today — your next great find is waiting.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="w-full sm:w-auto px-7 py-3.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      AllChinaBuy Sheet
                      <i className="ri-arrow-right-up-line"></i>
                    </a>
                    <Link
                      to="/"
                      className="w-full sm:w-auto px-7 py-3.5 bg-background-200 text-foreground-800 font-semibold text-sm rounded-full hover:bg-background-300 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      Back to Home
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-background-200/60">
                  <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4 text-center">More In-Depth Articles</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {articles.slice(0, 6).map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}`}
                        className="flex items-center gap-2 p-3 bg-background-100 rounded-xl border border-background-200/60 hover:border-primary-200/50 transition-all duration-200 cursor-pointer group"
                      >
                        <span className="text-xs text-foreground-700 group-hover:text-primary-600 transition-colors leading-tight flex-1">
                          {a.title.length > 52 ? a.title.slice(0, 52) + '...' : a.title}
                        </span>
                        <i className="ri-arrow-right-line text-xs text-foreground-400 group-hover:text-primary-500 flex-shrink-0"></i>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>

      {/* Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'The Ultimate AllChinaBuy Spreadsheet Guide for Smart Buyers in 2026',
            description: 'A comprehensive walkthrough of how to use the AllChinaBuy spreadsheet to find verified sellers, quality-checked products, and the best deals across 14 categories.',
            image: 'https://readdy.ai/api/search-image?query=Detailed%20flatlay%20of%20various%20fashion%20products%20including%20sneakers%20hoodies%20jackets%20and%20accessories%20arranged%20on%20cream%20background%20with%20notebook%20and%20checklist%2C%20warm%20natural%20lighting%2C%20minimalist%20lifestyle%20aesthetic%2C%20editorial%20product%20photography%20with%20rich%20textures%20and%20soft%20shadows%2C%20organized%20composition%20showing%20variety%20of%20items&width=1600&height=900&seq=guide-hero-2026&orientation=landscape',
            datePublished: '2026-06-25',
            dateModified: '2026-06-25',
            author: {
              '@type': 'Organization',
              name: 'AllChinaBuy Spreadsheet',
              url: `${SITE_URL}/`,
            },
            publisher: {
              '@type': 'Organization',
              name: 'AllChinaBuy Spreadsheet',
              url: `${SITE_URL}/`,
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${SITE_URL}/allchinabuy-spreadsheet-guide`,
            },
          }),
        }}
      />

      <Footer />
    </>
  );
}